import Box from '@mui/material/Box';

import { AppButton, RevealGroup } from '@/components/atoms';
import { SectionHeading } from '@/components/molecules';
import { Section } from '@/components/organisms';
import { introVehicles } from '@/config/introVehicles';
import { type Service } from '@/content/services';

import { SIBLINGS_TITLE_ID, serviceCountWord } from '../content';
import { ServiceCard } from '../molecules';

type Props = {
  siblings: readonly Service[];
  /** The stage these services share, named in the lede. */
  pillarTitle: string;
};

/**
 * The other services in the same stage of the import.
 *
 * Siblings rather than every other service, and that is the whole point of
 * grouping the catalogue: somebody reading about clearing is in the middle of
 * an import, and registration is another service that may be included in the
 * package. Offering them the whole catalogue would be a footer, which this
 * page already has.
 *
 * Renders nothing when a stage holds one service. Every stage holds at least
 * two today, so this is for the day the catalogue is edited — a heading
 * reading "the rest of this stage" over an empty grid is the failure that edit
 * produces, and the band declining to draw itself is cheaper than remembering.
 */
export function ServiceSiblingsBand({ siblings, pillarTitle }: Props) {
  if (siblings.length === 0) return null;

  return (
    <Section
      tone="muted"
      pattern="columns"
      labelledBy={SIBLINGS_TITLE_ID}
      backdrop={{ picture: introVehicles.noah.image, placement: 'right' }}
    >
      <SectionHeading
        id={SIBLINGS_TITLE_ID}
        eyebrow="Alongside this"
        title="The rest of this stage"
        lede={`What else happens while you are at the “${pillarTitle.toLowerCase()}” stage of an import.`}
      />

      <RevealGroup
        component="ul"
        step={60}
        sx={{
          listStyle: 'none',
          p: 0,
          m: 0,
          display: 'grid',
          gap: { xs: 4, md: 5 },
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
        }}
      >
        {siblings.map((service) => (
          <Box component="li" key={service.slug} sx={{ display: 'flex' }}>
            {/* `h3`, not the default `h4`: these cards sit directly under
                this band's own `h2`, with no pillar heading between. */}
            <ServiceCard service={service} level="h3" />
          </Box>
        ))}
      </RevealGroup>

      <Box sx={{ mt: { xs: 8, md: 10 } }}>
        <AppButton to="/services" appVariant="secondary">
          {`All ${serviceCountWord} services`}
        </AppButton>
      </Box>
    </Section>
  );
}
